import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import { useIsMobile } from '../../hooks/useIsMobile';

const SHOW_DELAY_MS = 5000;

const InstallPrompt: React.FC = () => {
  const { canInstall, isIOS, promptInstall, dismissPrompt } = useInstallPrompt();
  const isMobile = useIsMobile();
  const [visible, setVisible] = useState(false);
  const [iosGuideOpen, setIosGuideOpen] = useState(false);

  useEffect(() => {
    if (!canInstall || !isMobile) return;
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [canInstall, isMobile]);

  if (!visible || !canInstall || !isMobile) return null;

  const handleInstall = async () => {
    if (isIOS) {
      setIosGuideOpen(true);
      return;
    }
    const accepted = await promptInstall();
    if (accepted) setVisible(false);
  };

  const handleDismiss = () => {
    setVisible(false);
    dismissPrompt();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="install-prompt"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 35 }}
          className="fixed bottom-0 left-0 right-0 z-[500] px-4 pb-4"
          style={{
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-xl border border-gray-100 px-4 py-4 flex items-center gap-3"
            style={{ maxWidth: 480, margin: '0 auto' }}
          >
            {/* App icon */}
            <div className="w-12 h-12 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-lg">B</span>
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900 text-sm">Add BRAI to Home Screen</div>
              <div className="text-xs text-gray-500 mt-0.5">Get quick access from your home screen</div>
            </div>

            {/* Install button */}
            <button
              onClick={handleInstall}
              className="flex-shrink-0 bg-primary-600 text-white text-sm font-medium px-3 py-2 rounded-xl hover:bg-primary-700 active:scale-95 transition-all tap-scale"
            >
              <DownloadOutlined className="mr-1" />
              Add
            </button>

            {/* Dismiss */}
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 active:bg-gray-200 transition-colors tap-scale"
              aria-label="Dismiss"
            >
              <CloseOutlined className="text-xs" />
            </button>
          </div>

          {/* iOS guide */}
          {iosGuideOpen && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 bg-primary-50 border border-primary-200 rounded-xl px-4 py-3"
              style={{ maxWidth: 480, margin: '8px auto 0' }}
            >
              <div className="text-sm font-medium text-primary-800 mb-1">How to install on iOS</div>
              <ol className="text-xs text-primary-700 space-y-1 list-decimal list-inside">
                <li>Tap the <strong>Share</strong> button in Safari's toolbar</li>
                <li>Scroll down and tap <strong>Add to Home Screen</strong></li>
                <li>Tap <strong>Add</strong> in the top right corner</li>
              </ol>
              <button
                onClick={() => { setIosGuideOpen(false); handleDismiss(); }}
                className="mt-2 text-xs text-primary-600 underline tap-scale"
              >
                Got it
              </button>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InstallPrompt;
